import { PauseOutlined, PlayArrowOutlined } from '@mui/icons-material';
import React, { useRef, useState } from 'react';
// mui
import { Box, Slider, styled, Typography } from '@mui/material';
// components
import { LoadingButton } from '@mui/lab';
import ReactPlayer from 'react-player';

const CustomSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-thumb': {},
  '& .MuiSlider-rail': {
    color: theme.palette.grey[500],
  },
}));

const formatTime = (seconds: number) => {
  const date = new Date(seconds * 1000);
  const hh = date.getUTCHours();
  const mm = date.getUTCMinutes();
  const ss = date.getSeconds();
  if (hh) {
    // 00:00:00
    return `${hh}:${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
  }
  // 00:00
  return `${mm}:${ss.toString().padStart(2, '0')}`;
};

interface AudioPlayerProps {
  onGetUrl: () => Promise<string>;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ onGetUrl }) => {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const playerRef = useRef<ReactPlayer>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  const [url, setUrl] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handlePlayPause = async () => {
    if (!url) {
      setLoading(true);
      const fetchedUrl = await onGetUrl();
      setUrl(fetchedUrl);
      setLoading(false);
    }
    setPlaying(!playing);
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSliderMouseUp = () => {
    setIsDragging(false);
  };

  const handleSliderChange = (event: Event, value: number | number[]) => {
    setSliderValue(value as number);
    if (playerRef.current) {
      playerRef.current.seekTo(value as number);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center ' }}>
        <LoadingButton
          loading={loading}
          onClick={handlePlayPause}
          startIcon={playing ? <PauseOutlined /> : <PlayArrowOutlined />}
        >
          {playing ? 'Pause' : 'Play'}
        </LoadingButton>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            mx: 0.5,
            flexGrow: 1,
          }}
        >
          <Box sx={{ px: 0.5 }}>
            <CustomSlider
              size="small"
              min={0}
              max={duration}
              aria-label="time-indicator"
              value={sliderValue}
              onMouseDown={handleSliderMouseDown}
              onMouseUp={handleSliderMouseUp}
              onChange={handleSliderChange}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -1.5 }}>
            <Typography fontSize={10}>{formatTime(sliderValue)}</Typography>

            <Typography fontSize={10}>{formatTime(duration)}</Typography>
          </Box>
        </Box>
      </Box>

      <ReactPlayer
        style={{ display: 'none' }}
        url={url}
        ref={playerRef}
        controls={false}
        playing={playing}
        onEnded={() => setPlaying(false)}
        onProgress={(progress) => {
          if (!isDragging) {
            setSliderValue(progress.playedSeconds);
          }
        }}
        onDuration={(duration) => setDuration(duration)}
      />
    </>
  );
};

export default AudioPlayer;
